// src/components/common/filters/AnalyticsFilters.js
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useTheme } from '../../../contexts/ThemeContext';
import { ChevronDown, ChevronUp, Filter, X } from 'lucide-react';

/**
 * Analytics Filters Component
 * Provides comprehensive filtering options for analytics dashboard
 */
const AnalyticsFilters = ({
    onFilterChange,
    departments = [],
    designations = [],
    locations = [],
    employmentTypes = [],
    showDateRange = true,
    showPeriodSelector = true,
    showComparison = false
}) => {
    const { theme } = useTheme();
    const [filters, setFilters] = useState({
        period: 'month',
        startDate: format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-MM-dd'),
        endDate: format(new Date(), 'yyyy-MM-dd'),
        selectedDepartments: [],
        selectedDesignations: [],
        selectedLocations: [],
        selectedEmploymentTypes: [],
        compareWithPrevious: false
    });

    const [isCollapsed, setIsCollapsed] = useState(true);
    const [isExpanded, setIsExpanded] = useState(false);

    const periods = [
        { value: 'day', label: 'Daily' },
        { value: 'week', label: 'Weekly' },
        { value: 'month', label: 'Monthly' },
        { value: 'quarter', label: 'Quarterly' },
        { value: 'year', label: 'Yearly' },
        { value: 'custom', label: 'Custom Range' }
    ];

    const quickRanges = [
        { label: 'Today', days: 0 },
        { label: 'Last 7 Days', days: 7 },
        { label: 'Last 30 Days', days: 30 },
        { label: 'This Month', type: 'month' },
        { label: 'Last Month', type: 'last-month' },
        { label: 'This Quarter', type: 'quarter' },
        { label: 'This Year', type: 'year' }
    ];

    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);

        if (onFilterChange) {
            onFilterChange(newFilters);
        }
    };

    const handleMultiSelect = (key, value) => {
        const currentValues = filters[key];
        const newValues = currentValues.includes(value)
            ? currentValues.filter(v => v !== value)
            : [...currentValues, value];

        handleFilterChange(key, newValues);
    };

    const handleQuickRange = (range) => {
        const today = new Date();
        let startDate, endDate;

        if (range.days !== undefined) {
            if (range.days === 0) {
                startDate = endDate = format(today, 'yyyy-MM-dd');
            } else {
                endDate = format(today, 'yyyy-MM-dd');
                const start = new Date(today);
                start.setDate(start.getDate() - range.days);
                startDate = format(start, 'yyyy-MM-dd');
            }
        } else if (range.type === 'month') {
            const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
            startDate = format(firstDay, 'yyyy-MM-dd');
            endDate = format(today, 'yyyy-MM-dd');
        } else if (range.type === 'last-month') {
            const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
            startDate = format(firstDay, 'yyyy-MM-dd');
            endDate = format(lastDay, 'yyyy-MM-dd');
        } else if (range.type === 'quarter') {
            const quarter = Math.floor(today.getMonth() / 3);
            const firstDay = new Date(today.getFullYear(), quarter * 3, 1);
            startDate = format(firstDay, 'yyyy-MM-dd');
            endDate = format(today, 'yyyy-MM-dd');
        } else if (range.type === 'year') {
            const firstDay = new Date(today.getFullYear(), 0, 1);
            startDate = format(firstDay, 'yyyy-MM-dd');
            endDate = format(today, 'yyyy-MM-dd');
        }

        setFilters(prev => ({ ...prev, startDate, endDate, period: 'custom' }));
        if (onFilterChange) {
            onFilterChange({ ...filters, startDate, endDate, period: 'custom' });
        }
    };

    const clearAllFilters = () => {
        const defaultFilters = {
            period: 'month',
            startDate: format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-MM-dd'),
            endDate: format(new Date(), 'yyyy-MM-dd'),
            selectedDepartments: [],
            selectedDesignations: [],
            selectedLocations: [],
            selectedEmploymentTypes: [],
            compareWithPrevious: false
        };
        setFilters(defaultFilters);
        if (onFilterChange) {
            onFilterChange(defaultFilters);
        }
    };

    const hasActiveFilters = () => {
        return filters.selectedDepartments.length > 0 ||
               filters.selectedDesignations.length > 0 ||
               filters.selectedLocations.length > 0 ||
               filters.selectedEmploymentTypes.length > 0;
    };

    return (
        <div className={`rounded-lg shadow-sm border mb-6 ${
            theme === 'dark'
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'
        }`}>
            {/* Collapsible Header */}
            <div
                className={`flex items-center justify-between p-4 cursor-pointer ${
                    theme === 'dark' ? 'hover:bg-gray-750' : 'hover:bg-gray-50'
                }`}
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <div className="flex items-center gap-3">
                    <Filter className={`w-5 h-5 ${
                        theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <h3 className={`text-lg font-semibold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        Filters
                    </h3>
                    {hasActiveFilters() && (
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            theme === 'dark'
                                ? 'bg-blue-900 text-blue-200'
                                : 'bg-blue-100 text-blue-700'
                        }`}>
                            {filters.selectedDepartments.length +
                             filters.selectedDesignations.length +
                             filters.selectedLocations.length +
                             filters.selectedEmploymentTypes.length} active
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {hasActiveFilters() && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                clearAllFilters();
                            }}
                            className={`text-sm font-medium ${
                                theme === 'dark'
                                    ? 'text-blue-400 hover:text-blue-300'
                                    : 'text-blue-600 hover:text-blue-800'
                            }`}
                        >
                            Clear All
                        </button>
                    )}
                    {isCollapsed ? (
                        <ChevronDown className={`w-5 h-5 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`} />
                    ) : (
                        <ChevronUp className={`w-5 h-5 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`} />
                    )}
                </div>
            </div>

            {/* Collapsible Content */}
            {!isCollapsed && (
                <div className={`p-4 border-t ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>

                    {/* Quick Ranges */}
                    {showDateRange && (
                        <div className="mb-4">
                            <label className={`block text-sm font-medium mb-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                                Quick Ranges
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {quickRanges.map((range) => (
                                    <button
                                        key={range.label}
                                        onClick={() => handleQuickRange(range)}
                                        className={`px-3 py-1 text-sm rounded-md border transition-colors ${
                                            theme === 'dark'
                                                ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                        }`}
                                    >
                                        {range.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Period Selector and Date Range */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        {showPeriodSelector && (
                            <div>
                                <label className={`block text-sm font-medium mb-2 ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    Period
                                </label>
                                <select
                                    value={filters.period}
                                    onChange={(e) => handleFilterChange('period', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white'
                                            : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                >
                                    {periods.map((period) => (
                                        <option key={period.value} value={period.value}>
                                            {period.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {showDateRange && (
                            <>
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                        Start Date
                                    </label>
                                    <input
                                        type="date"
                                        value={filters.startDate}
                                        onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                    />
                                </div>
                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                        End Date
                                    </label>
                                    <input
                                        type="date"
                                        value={filters.endDate}
                                        onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    {/* Comparison Toggle */}
                    {showComparison && (
                        <div className="mb-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={filters.compareWithPrevious}
                                    onChange={(e) => handleFilterChange('compareWithPrevious', e.target.checked)}
                                    className={`w-4 h-4 rounded focus:ring-2 focus:ring-blue-500 ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-blue-600'
                                            : 'bg-white border-gray-300 text-blue-600'
                                    }`}
                                />
                                <span className={`text-sm font-medium ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    Compare with previous period
                                </span>
                            </label>
                        </div>
                    )}

                    {/* Advanced Filters Toggle */}
                    <div className="mb-4">
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className={`text-sm font-medium flex items-center gap-2 ${
                                theme === 'dark'
                                    ? 'text-blue-400 hover:text-blue-300'
                                    : 'text-blue-600 hover:text-blue-800'
                            }`}
                        >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            {isExpanded ? 'Hide' : 'Show'} Advanced Filters
                        </button>
                    </div>

                    {/* Advanced Filters (Collapsible) */}
                    {isExpanded && (
                        <div className={`border-t pt-4 mt-4 ${
                            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                        }`}>
                            <h4 className={`text-sm font-semibold mb-3 ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>Advanced Filters</h4>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                {/* Department Filter */}
                                {departments.length > 0 && (
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                            Departments ({filters.selectedDepartments.length})
                                        </label>
                                        <div className={`max-h-40 overflow-y-auto border rounded-md p-2 ${
                                            theme === 'dark'
                                                ? 'border-gray-600 bg-gray-750'
                                                : 'border-gray-300 bg-white'
                                        }`}>
                                            {departments.map((dept) => (
                                                <label key={dept} className={`flex items-center gap-2 py-1 cursor-pointer rounded ${
                                                    theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                                }`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={filters.selectedDepartments.includes(dept)}
                                                        onChange={() => handleMultiSelect('selectedDepartments', dept)}
                                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                    />
                                                    <span className={`text-sm ${
                                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                    }`}>{dept}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Designation Filter */}
                                {designations.length > 0 && (
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                            Designations ({filters.selectedDesignations.length})
                                        </label>
                                        <div className={`max-h-40 overflow-y-auto border rounded-md p-2 ${
                                            theme === 'dark'
                                                ? 'border-gray-600 bg-gray-750'
                                                : 'border-gray-300 bg-white'
                                        }`}>
                                            {designations.map((designation) => (
                                                <label key={designation} className={`flex items-center gap-2 py-1 cursor-pointer rounded ${
                                                    theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                                }`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={filters.selectedDesignations.includes(designation)}
                                                        onChange={() => handleMultiSelect('selectedDesignations', designation)}
                                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                    />
                                                    <span className={`text-sm ${
                                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                    }`}>{designation}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Location Filter */}
                                {locations.length > 0 && (
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                            Locations ({filters.selectedLocations.length})
                                        </label>
                                        <div className={`max-h-40 overflow-y-auto border rounded-md p-2 ${
                                            theme === 'dark'
                                                ? 'border-gray-600 bg-gray-750'
                                                : 'border-gray-300 bg-white'
                                        }`}>
                                            {locations.map((location) => (
                                                <label key={location} className={`flex items-center gap-2 py-1 cursor-pointer rounded ${
                                                    theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                                }`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={filters.selectedLocations.includes(location)}
                                                        onChange={() => handleMultiSelect('selectedLocations', location)}
                                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                    />
                                                    <span className={`text-sm ${
                                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                    }`}>{location}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Employment Type Filter */}
                                {employmentTypes.length > 0 && (
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                            Employment Types ({filters.selectedEmploymentTypes.length})
                                        </label>
                                        <div className={`max-h-40 overflow-y-auto border rounded-md p-2 ${
                                            theme === 'dark'
                                                ? 'border-gray-600 bg-gray-750'
                                                : 'border-gray-300 bg-white'
                                        }`}>
                                            {employmentTypes.map((type) => (
                                                <label key={type} className={`flex items-center gap-2 py-1 cursor-pointer rounded ${
                                                    theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                                }`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={filters.selectedEmploymentTypes.includes(type)}
                                                        onChange={() => handleMultiSelect('selectedEmploymentTypes', type)}
                                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                    />
                                                    <span className={`text-sm ${
                                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                    }`}>{type}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Active Filters Summary */}
                    {hasActiveFilters() && (
                        <div className={`mt-4 pt-4 border-t ${
                            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                        }`}>
                            <div className="flex flex-wrap gap-2">
                                {filters.selectedDepartments.map((dept) => (
                                    <span key={dept} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                                        theme === 'dark'
                                            ? 'bg-blue-900 text-blue-200'
                                            : 'bg-blue-100 text-blue-800'
                                    }`}>
                                        {dept}
                                        <button
                                            onClick={() => handleMultiSelect('selectedDepartments', dept)}
                                            className={`ml-1 ${
                                                theme === 'dark' ? 'hover:text-blue-100' : 'hover:text-blue-900'
                                            }`}
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                                {filters.selectedDesignations.map((designation) => (
                                    <span key={designation} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                                        theme === 'dark'
                                            ? 'bg-green-900 text-green-200'
                                            : 'bg-green-100 text-green-800'
                                    }`}>
                                        {designation}
                                        <button
                                            onClick={() => handleMultiSelect('selectedDesignations', designation)}
                                            className={`ml-1 ${
                                                theme === 'dark' ? 'hover:text-green-100' : 'hover:text-green-900'
                                            }`}
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                                {filters.selectedLocations.map((location) => (
                                    <span key={location} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                                        theme === 'dark'
                                            ? 'bg-yellow-900 text-yellow-200'
                                            : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                        {location}
                                        <button
                                            onClick={() => handleMultiSelect('selectedLocations', location)}
                                            className={`ml-1 ${
                                                theme === 'dark' ? 'hover:text-yellow-100' : 'hover:text-yellow-900'
                                            }`}
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                                {filters.selectedEmploymentTypes.map((type) => (
                                    <span key={type} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                                        theme === 'dark'
                                            ? 'bg-purple-900 text-purple-200'
                                            : 'bg-purple-100 text-purple-800'
                                    }`}>
                                        {type}
                                        <button
                                            onClick={() => handleMultiSelect('selectedEmploymentTypes', type)}
                                            className={`ml-1 ${
                                                theme === 'dark' ? 'hover:text-purple-100' : 'hover:text-purple-900'
                                            }`}
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AnalyticsFilters;
